import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import styles from './OssMp3Combine.module.css';

function normalizeChapter(item) {
  if (!item) {
    return null;
  }
  const missing =
    Array.isArray(item.missing_files)
      ? item.missing_files
      : Array.isArray(item.missingFiles)
        ? item.missingFiles
        : [];
  const hasRequired =
    typeof item.has_required_files === 'boolean'
      ? item.has_required_files
      : Boolean(item.hasRequiredFiles);
  const found = new Set(
    Array.isArray(item.found_files)
      ? item.found_files
      : Array.isArray(item.foundFiles)
        ? item.foundFiles
        : [],
  );
  const hasCombined = found.has('en_cn.mp3') && found.has('cn_en.mp3');
  return {
    name: String(item.name ?? item.prefix ?? ''),
    prefix: String(item.prefix ?? ''),
    hasRequiredFiles: hasRequired,
    missingFiles: missing.map((entry) => String(entry)),
    foundFiles: found,
    hasCombined,
  };
}

const PAGE_SIZE = 500;

function OssMp3Combine() {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [isCombining, setIsCombining] = useState(false);
  const [combineError, setCombineError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [result, setResult] = useState(null);
  const [pauseBetweenSegments, setPauseBetweenSegments] = useState(150);
  const [pauseBetweenPairs, setPauseBetweenPairs] = useState(300);
  const [searchTerm, setSearchTerm] = useState('');

  /* ── 分页 ── */
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  /* ── 一键混音 ── */
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, failed: 0 });
  const batchCancelledRef = useRef(false);

  const loadChapters = useCallback(async (page = 1, refresh = false) => {
    setLoading(true);
    setError('');
    const offset = (page - 1) * PAGE_SIZE;
    try {
      const { data } = await api.get('/oss-mp3/chapters', {
        params: { prefix: 'audios', offset, limit: PAGE_SIZE, refresh },
        timeout: 120000,
      });
      const list = Array.isArray(data?.chapters) ? data.chapters : [];
      const normalized = list
        .map(normalizeChapter)
        .filter(Boolean)
        .sort((a, b) => {
          const left = Number.parseInt(a.name, 10);
          const right = Number.parseInt(b.name, 10);
          if (Number.isFinite(left) && Number.isFinite(right)) {
            return left - right;
          }
          return a.name.localeCompare(b.name);
        });
      setChapters(normalized);
      setTotalCount(data?.total_count ?? normalized.length);
      setCurrentPage(page);
      setSelectedChapter((prev) => {
        if (prev && normalized.some((item) => item.prefix === prev.prefix)) {
          return normalized.find((item) => item.prefix === prev.prefix) ?? null;
        }
        return normalized.length > 0 ? normalized[0] : null;
      });
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '获取 OSS 章节列表失败，请稍后重试';
      setError(message);
      setChapters([]);
      setSelectedChapter(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChapters(1);
  }, [loadChapters]);

  const handlePageChange = useCallback((page) => {
    if (page < 1 || page > totalPages || loading) return;
    loadChapters(page);
  }, [totalPages, loading, loadChapters]);

  const handleRefresh = useCallback(() => {
    loadChapters(currentPage, true);
  }, [currentPage, loadChapters]);

  const filteredChapters = useMemo(() => {
    if (!searchTerm.trim()) {
      return chapters;
    }
    const keyword = searchTerm.trim().toLowerCase();
    return chapters.filter((item) => {
      return (
        item.name.toLowerCase().includes(keyword) ||
        item.prefix.toLowerCase().includes(keyword)
      );
    });
  }, [chapters, searchTerm]);

  const handleSelectChapter = (chapter) => {
    setSelectedChapter(chapter);
    setCombineError('');
    setSuccessMessage('');
    setResult(null);
  };

  const handleCombine = async () => {
    if (!selectedChapter) {
      setCombineError('请先选择章节');
      return;
    }
    if (!selectedChapter.hasRequiredFiles) {
      setCombineError('当前章节缺少必要的四个 OSS 文件，无法生成混合音频');
      return;
    }

    setCombineError('');
    setSuccessMessage('');
    setResult(null);
    setIsCombining(true);

    const chapterIdNumber = Number.parseInt(selectedChapter.name, 10);
    const payload = {
      oss_prefix: selectedChapter.prefix,
      pause_between_segments_ms: Number.parseInt(pauseBetweenSegments, 10) || 0,
      pause_between_pairs_ms: Number.parseInt(pauseBetweenPairs, 10) || 0,
      overwrite: true,
    };
    if (Number.isFinite(chapterIdNumber) && chapterIdNumber > 0) {
      payload.chapter_id = chapterIdNumber;
    }

    try {
      const { data } = await api.post('/oss-mp3/combine', payload, { timeout: 300000 });
      setResult(data);
      setSuccessMessage('混合音频与字幕生成成功，已上传至 OSS');
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        '生成混合音频失败，请稍后重试';
      setCombineError(message);
    } finally {
      setIsCombining(false);
    }
  };

  const pendingChapters = useMemo(
    () => chapters.filter((ch) => ch.hasRequiredFiles && !ch.hasCombined),
    [chapters],
  );

  const handleBatchCombine = useCallback(async () => {
    if (pendingChapters.length === 0) return;

    setBatchRunning(true);
    batchCancelledRef.current = false;
    setBatchProgress({ done: 0, total: pendingChapters.length, failed: 0 });

    let done = 0;
    let failed = 0;

    for (const chapter of pendingChapters) {
      if (batchCancelledRef.current) break;

      const chapterIdNumber = Number.parseInt(chapter.name, 10);
      const payload = {
        oss_prefix: chapter.prefix,
        pause_between_segments_ms: Number.parseInt(pauseBetweenSegments, 10) || 0,
        pause_between_pairs_ms: Number.parseInt(pauseBetweenPairs, 10) || 0,
        overwrite: false,
      };
      if (Number.isFinite(chapterIdNumber) && chapterIdNumber > 0) {
        payload.chapter_id = chapterIdNumber;
      }

      try {
        await api.post('/oss-mp3/combine', payload, { timeout: 300000 });
      } catch {
        failed += 1;
      }

      done += 1;
      setBatchProgress({ done, total: pendingChapters.length, failed });
    }

    setBatchRunning(false);
    // 完成后刷新当前页
    loadChapters(currentPage, true);
  }, [pendingChapters, pauseBetweenSegments, pauseBetweenPairs, loadChapters, currentPage]);

  const handleBatchCancel = useCallback(() => {
    batchCancelledRef.current = true;
  }, []);

  const renderStatus = (chapter) => {
    if (!chapter?.hasRequiredFiles) {
      return (
        <span className={`${styles.statusTag} ${styles.statusWarn}`}>
          缺少文件
        </span>
      );
    }
    if (chapter.hasCombined) {
      return (
        <span className={`${styles.statusTag} ${styles.statusDone}`}>
          已完成
        </span>
      );
    }
    return (
      <span className={`${styles.statusTag} ${styles.statusOk}`}>
        可生成
      </span>
    );
  };

  const renderResultLinks = (variant, label) => {
    if (!variant) {
      return null;
    }
    return (
      <div className={styles.resultSection}>
        <strong>{label}</strong>
        <div className={styles.linkRow}>
          <span>音频：</span>
          <a href={variant.audio?.url} target="_blank" rel="noreferrer">
            {variant.audio?.path}
          </a>
        </div>
        <div className={styles.linkRow}>
          <span>字幕：</span>
          <a href={variant.srt?.url} target="_blank" rel="noreferrer">
            {variant.srt?.path}
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <div>
            <h2>OSS 混合音频生成</h2>
            <p className={styles.subtitle}>
              自动从 OSS 下载 en/cn 字幕与音频，合成交错语音并保存到服务器本地
            </p>
          </div>
          <div className={styles.actions}>
            <input
              className={styles.searchInput}
              placeholder="搜索章节 ID 或前缀..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <button
              type="button"
              className={styles.refreshButton}
              onClick={handleRefresh}
              disabled={loading || batchRunning}
            >
              {loading ? '刷新中...' : '刷新列表'}
            </button>
            {!batchRunning ? (
              <button
                type="button"
                className={styles.batchButton}
                onClick={handleBatchCombine}
                disabled={loading || isCombining || pendingChapters.length === 0}
              >
                一键混音（{pendingChapters.length}）
              </button>
            ) : (
              <button
                type="button"
                className={styles.batchCancelButton}
                onClick={handleBatchCancel}
              >
                停止
              </button>
            )}
          </div>
        </div>
        {batchRunning && (
          <div className={styles.batchProgressRow}>
            <div className={styles.batchProgressBar}>
              <div
                className={styles.batchProgressFill}
                style={{ width: `${batchProgress.total > 0 ? Math.round((batchProgress.done / batchProgress.total) * 100) : 0}%` }}
              />
            </div>
            <span className={styles.batchProgressText}>
              {batchProgress.done}/{batchProgress.total}
              {batchProgress.failed > 0 && (
                <span className={styles.batchFailText}>（{batchProgress.failed} 失败）</span>
              )}
            </span>
          </div>
        )}
        {!batchRunning && batchProgress.total > 0 && batchProgress.done === batchProgress.total && (
          <div className={styles.success}>
            一键混音完成：成功 {batchProgress.total - batchProgress.failed}/{batchProgress.total}
            {batchProgress.failed > 0 && `，失败 ${batchProgress.failed}`}
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.layout}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <h3>章节列表</h3>
            <span className={styles.totalInfo}>共 {totalCount} 个章节</span>
          </div>
          <div className={styles.chapterList}>
            {filteredChapters.length === 0 && !loading && (
              <div>暂无可用章节</div>
            )}
            {loading && <div className={styles.loadingHint}>加载中…</div>}
            {filteredChapters.map((chapter) => {
              const isSelected = selectedChapter?.prefix === chapter.prefix;
              return (
                <button
                  key={chapter.prefix}
                  type="button"
                  className={`${styles.chapterItem} ${
                    isSelected ? styles.chapterItemSelected : ''
                  }`}
                  onClick={() => handleSelectChapter(chapter)}
                >
                  <span className={styles.chapterName}>章节 {chapter.name}</span>
                  <span className={styles.chapterPrefix}>{chapter.prefix}</span>
                  {renderStatus(chapter)}
                </button>
              );
            })}
          </div>
          {/* ── 分页控件 ── */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => handlePageChange(1)}
                disabled={currentPage <= 1 || loading}
              >
                首页
              </button>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1 || loading}
              >
                上一页
              </button>
              <span className={styles.pageInfo}>
                第 {currentPage} / {totalPages} 页
              </span>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || loading}
              >
                下一页
              </button>
              <button
                type="button"
                className={styles.pageBtn}
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages || loading}
              >
                末页
              </button>
            </div>
          )}
        </div>

        <div className={styles.panel}>
          <h3>生成设置</h3>
          {selectedChapter ? (
            <div className={styles.details}>
              <div className={styles.detailRow}>
                <label>章节编号</label>
                <span>{selectedChapter.name}</span>
              </div>
              <div className={styles.detailRow}>
                <label>OSS 前缀</label>
                <span>{selectedChapter.prefix}</span>
              </div>
              {!selectedChapter.hasRequiredFiles && (
                <div className={styles.detailRow}>
                  <label>缺失文件</label>
                  <div className={styles.missingList}>
                    {selectedChapter.missingFiles.length === 0
                      ? '未知'
                      : selectedChapter.missingFiles.map((file) => (
                          <span key={file} className={styles.missingItem}>
                            {file}
                          </span>
                        ))}
                  </div>
                </div>
              )}
              <div className={styles.detailRow}>
                <label>句内静默(ms)</label>
                <input
                  type="number"
                  className={styles.input}
                  value={pauseBetweenSegments}
                  min="0"
                  onChange={(event) => setPauseBetweenSegments(event.target.value)}
                />
              </div>
              <div className={styles.detailRow}>
                <label>句间静默(ms)</label>
                <input
                  type="number"
                  className={styles.input}
                  value={pauseBetweenPairs}
                  min="0"
                  onChange={(event) => setPauseBetweenPairs(event.target.value)}
                />
              </div>
              <button
                type="button"
                className={styles.combineButton}
                onClick={handleCombine}
                disabled={isCombining}
              >
                {isCombining ? '生成中...' : '生成混合音频'}
              </button>

              {combineError && <div className={styles.error}>{combineError}</div>}
              {successMessage && <div className={styles.success}>{successMessage}</div>}

              {result && (
                <div className={styles.resultCard}>
                  <div>
                    已处理句子：EN {result.en_sentence_count} / CN {result.cn_sentence_count}
                  </div>
                  {renderResultLinks(result.en_cn, '英文优先 (en_cn)')}
                  {renderResultLinks(result.cn_en, '中文优先 (cn_en)')}
                </div>
              )}
            </div>
          ) : (
            <div>请选择章节查看详情</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OssMp3Combine;
